/**
 * <promo-product-carousel>
 * ------------------------
 * Promotional banner + horizontally scrollable product shelf with AJAX add to
 * cart. Rendered by snippets/promo-product-carousel.liquid, which is shared by
 * the standalone "Promo Product Carousel" section and the "Promo Product
 * Carousel" block of the revamp collection product grid.
 *
 * Implemented as a custom element rather than an inline IIFE on purpose:
 * FacetFiltersForm.renderProductGridContainer() replaces
 * #ProductGridContainer.innerHTML on every filter change, and <script> tags
 * inserted through innerHTML are never executed. A custom element upgrades
 * itself whenever its markup is (re)inserted, so the block keeps working after
 * filtering, load-more and theme editor re-renders.
 *
 * No external libraries, no jQuery.
 */
if (!customElements.get('promo-product-carousel')) {
  class PromoProductCarousel extends HTMLElement {
    connectedCallback() {
      // connectedCallback fires again if the node is moved in the DOM.
      // Re-arm autoplay, but never bind the listeners twice.
      if (this.pcInitialised) {
        this.startAutoplay();
        return;
      }
      this.pcInitialised = true;

      this.track = this.querySelector('[data-pc-track]');
      this.statusEl = this.querySelector('[data-pc-status]');
      this.prevBtn = this.querySelector('[data-pc-prev]');
      this.nextBtn = this.querySelector('[data-pc-next]');
      if (!this.track) return;

      this.routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      this.autoplayTimer = null;
      this.autoplayOn = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 4000;

      this.bindCarousel();
      this.bindAutoplay();
      this.bindAddToCart();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    /* ---------- Carousel scrolling ---------- */

    step() {
      const card = this.track.querySelector('.pc__card');
      if (!card) return this.track.clientWidth;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    atStart() {
      return this.track.scrollLeft <= 2;
    }

    atEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }

    get loops() {
      return this.dataset.loop === 'true';
    }

    syncArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      this.prevBtn.disabled = !this.loops && this.atStart();
      this.nextBtn.disabled = !this.loops && this.atEnd();
    }

    scrollNext() {
      if (this.atEnd()) {
        if (this.loops) this.track.scrollTo({ left: 0 });
        return;
      }
      this.track.scrollBy({ left: this.step() });
    }

    scrollPrev() {
      if (this.atStart()) {
        if (this.loops) this.track.scrollTo({ left: this.track.scrollWidth });
        return;
      }
      this.track.scrollBy({ left: -this.step() });
    }

    bindCarousel() {
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.stopAutoplay();
          this.scrollNext();
        });
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.stopAutoplay();
          this.scrollPrev();
        });
      }

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.stopAutoplay();
          this.scrollNext();
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.stopAutoplay();
          this.scrollPrev();
        }
      });

      let scrollTimer;
      this.track.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => this.syncArrows(), 80);
        },
        { passive: true }
      );

      this.resizeHandler = () => this.syncArrows();
      window.addEventListener('resize', this.resizeHandler);

      this.syncArrows();
    }

    /* ---------- Autoplay ---------- */

    startAutoplay() {
      if (!this.autoplayOn || this.autoplayTimer || !this.track) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.autoplayTimer = window.setInterval(() => {
        if (document.hidden) return;
        if (this.atEnd()) {
          if (this.loops) this.track.scrollTo({ left: 0 });
          else this.stopAutoplay();
          return;
        }
        this.track.scrollBy({ left: this.step() });
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    bindAutoplay() {
      if (!this.autoplayOn) return;

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });

      this.startAutoplay();
    }

    /* ----------------------------------------------------------------
       Tracking
       ----------------------------------------------------------------
       Clicks (banner / product card / add-to-cart / arrows) are picked up by
       the site-wide delegated listener in the 'datalayer-events' snippet,
       which reads the data-* attributes on .section-item-tracked elements and
       pushes the CleverTap "section_clicked" event. Nothing extra is needed.

       For the cart we re-use the theme's site-wide 'product:added' event (same
       contract as assets/product-form.js) so the existing addToCart /
       add_to_cart / Cart Updated pushes fire for this section too. Wrapped in
       try/catch so analytics can never break the cart flow.
    ---------------------------------------------------------------- */
    trackProductAdded(button, json) {
      try {
        // The site-wide listener matches on response.variant_id, so hand it a
        // line item — /cart/add.js returns { items: [...] } for the array form.
        const lineItem = json && json.items && json.items.length ? json.items[0] : json;

        document.dispatchEvent(
          new CustomEvent('product:added', {
            detail: {
              response: lineItem,
              sectionTitle: this.dataset.sectionTitle || '',
              blockTitle: button.dataset.blockTitle || button.dataset.productTitle || 'Default',
              collectionName: this.dataset.collectionName || 'NA'
            }
          })
        );
      } catch (error) {
        console.warn('[promo-product-carousel] tracking failed', error);
      }
    }

    /* ---------- AJAX add to cart ---------- */

    refreshCart(data) {
      const drawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
        drawer.classList.remove('is-empty');
        try {
          drawer.renderContents(data);
          return;
        } catch (error) {
          /* fall through to the bubble-only update below */
        }
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data && data.sections && data.sections['cart-icon-bubble']) {
        try {
          bubble.innerHTML = new DOMParser()
            .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section').innerHTML;
        } catch (error) {
          /* no-op */
        }
      }
    }

    bindAddToCart() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pc-atc]');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        if (button.disabled || button.dataset.pcLoading === 'true') return;

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const defaultText = button.dataset.defaultText || 'Add to Cart';
        button.dataset.pcLoading = 'true';
        button.disabled = true;
        button.textContent = 'Adding...';
        if (this.statusEl) this.statusEl.textContent = 'Adding item to cart';

        fetch(this.routes + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname
          })
        })
          .then((response) =>
            response.json().then((json) => {
              if (!response.ok) throw new Error(json.description || json.message || 'Add to cart failed');
              return json;
            })
          )
          .then((json) => {
            button.textContent = 'Added';
            if (this.statusEl) this.statusEl.textContent = 'Item added to cart';
            this.trackProductAdded(button, json);
            this.refreshCart(json);
            window.setTimeout(() => {
              button.textContent = defaultText;
              button.disabled = false;
              button.dataset.pcLoading = 'false';
            }, 1500);
          })
          .catch((error) => {
            button.textContent = defaultText;
            button.disabled = false;
            button.dataset.pcLoading = 'false';
            if (this.statusEl) {
              this.statusEl.textContent = (error && error.message) || 'Could not add this item to the cart.';
            }
            console.error('[promo-product-carousel]', error);
          });
      });
    }
  }

  customElements.define('promo-product-carousel', PromoProductCarousel);
}
